import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { DeckReadPass, DeckWork } from './types';

/*
 * The first read: a prelude, seven passes, a close. One direction only.
 *
 * The passes run concurrently on the server and land in whatever order their
 * pages answer, which is nothing like the order they are shown in. An earlier
 * build let those landings choose which card held the frame, and a landing is
 * not a direction: the frame jumped to pass four, came back to pass two, and
 * the dot strip lit backwards with it. So the sequence is owned HERE and moves
 * one way:
 *
 *   prelude -> pass 1 -> 2 -> 3 -> 4 -> 5 -> 6 -> 7 -> the close -> the deck
 *
 * `at` is the whole state machine and it only ever counts up. Server events
 * are data: they fill in what a pass is reading and what it found, and they
 * are drawn wherever the sequence happens to be. They never move it.
 *
 * Two properties this file exists to hold:
 *
 *   Every pass is shown.  The time left is divided by the passes left, so a
 *                         slow prelude shortens the rest evenly instead of
 *                         deleting the last three. It always reaches seven.
 *   Nothing stalls.       The probe and the map are their own stage, named as
 *                         what they are, rather than being charged to pass one
 *                         and read as a pass that hung.
 *
 * Pacing decides WHICH pass holds the frame. It never decides what that pass
 * claims to have found: a card says only what is true of its pass at the
 * moment it is on screen, and a result appears when the server reports it.
 */

const LANDED = ['done', 'empty', 'failed'];

/** The prelude: the probe and the map, before any pass can honestly start. */
const PRELUDE_CAP = 6500;
/** A beat to read the map's result before the passes begin. */
const AFTER_MAP = 900;
/** The band one pass may hold the frame for. */
const FLOOR = 1700;
const CEIL = 3000;
/** A pass that lands while its card is up stays this long past the landing. */
const AFTER_LANDING = 1100;
/** The seven passes' share of the walk. The close comes out of its own budget. */
const BUDGET = 27000;
/** The closing card, and one line of it per late result. */
const CLOSE_HOLD = 2400;
const CLOSE_LINE = 700;
const CLOSE_LINES = 3;
/** The slide itself. Matches the .odeck-pass enter and leave animations. */
const SLIDE = 280;
/** How often the sequence asks itself whether the frame should move on. */
const TICK = 120;

/** Before the first pass. Below zero so `at` counts up from the prelude. */
const PRELUDE = -1;

const landed = (pass: DeckReadPass): boolean => LANDED.includes(pass.state);

const clamp = (value: number, low: number, high: number): number => Math.min(high, Math.max(low, value));

const plural = (n: number): string => {
  if (n === 1) return '';
  return 's';
};

/*
 * A dot's state is read off the cursor, never off the server.
 *
 * Grey is still to come, yellow is the pass on screen, black is a pass the
 * sequence has been through. `settled` only ever grows and `active` is the one
 * pass on screen, so the strip can only fill left to right.
 *
 * A pass whose page does not exist, or whose read failed, is still a pass the
 * sequence has been through: it goes black and hollow rather than reverting to
 * grey, because a dot that empties out behind the frame reads as progress
 * running backwards. A pass the sequence never reached stays grey, including
 * on a read that stopped early: seven black dots would claim seven passes that
 * never ran.
 */
const dotClass = (index: number, active: number, settled: number, pass: DeckReadPass): string => {
  if (index === active) return 'doing';
  if (index >= settled) return 'waiting';
  if (pass.state === 'empty') return 'done hollow';
  if (pass.state === 'failed') return 'done hollow';
  return 'done';
};

// The server names a page by its path, and the homepage's path is "/". A bare
// slash on a card tells a person nothing, so it is read back as what it is.
const pathWord = (path: string): string => {
  if (path === '/') return 'the homepage';
  return path;
};

const pageWord = (pass: DeckReadPass): string => {
  if (pass.paths && pass.paths.length > 0) return pass.paths.map(pathWord).join(' · ');
  if (pass.pages > 0) return `${pass.pages} page${plural(pass.pages)}`;
  // Before the server has said which pages this pass gets, we do not know
  // whether the site has them. Saying it has none would be a guess.
  if (pass.state === 'waiting') return 'queued';
  return 'no page for this on the site';
};

/* What a pass says while it is still reading. */
const waitWord = (pass: DeckReadPass): string => {
  if (pass.state === 'waiting') return 'Orbit is choosing the pages';
  return 'Orbit is extracting';
};

/* The face of one pass. Two states: extracting, then what it found. */
const PassFace: React.FC<{ pass: DeckReadPass; leaving?: boolean }> = ({ pass, leaving = false }) => {
  const reading = !landed(pass);
  return (
    <div className={clsx('odeck-pass', leaving && 'leave')}>
      <span className={clsx('odeck-read-ring', reading && 'spin', !reading && 'done')} aria-hidden><i /></span>
      <strong>{pass.title}</strong>
      <small>WHY · {pass.why}</small>
      <span className="odeck-read-at">{pageWord(pass)}</span>
      {reading && <em className="odeck-read-wait">{waitWord(pass)}</em>}
      {!reading && pass.line && (
        <em className="odeck-read-got">
          → {pass.line}
          {pass.found > 0 && <b>{pass.found} field{plural(pass.found)}</b>}
        </em>
      )}
      {!reading && !pass.line && pass.state === 'empty' && <em className="odeck-read-none">no such page on this site</em>}
      {!reading && !pass.line && pass.state !== 'empty' && <em className="odeck-read-none">nothing this page could answer</em>}
    </div>
  );
};

/*
 * The prelude: opening the site, in the steps the server actually takes.
 *
 * This is the wait nothing can fill, so it is named rather than hidden behind
 * a pass that appears to hang. The seven dots stay grey throughout: none of
 * them has started.
 */
const PreludeFace: React.FC<{ work: DeckWork; ready: boolean; leaving?: boolean }> = ({ work, ready, leaving = false }) => {
  const steps = (work.steps || []).filter((s) => s.key === 'probe' || s.key === 'map');
  return (
    <div className={clsx('odeck-pass', leaving && 'leave')}>
      <span className={clsx('odeck-read-ring', !ready && 'spin')} aria-hidden><i /></span>
      <strong>Opening {work.site || 'the site'}</strong>
      <small>WHY · ORBIT READS ONLY PAGES THAT EXIST</small>
      <ol className="odeck-read-steps">
        {steps.map((step) => (
          <li key={`${step.key}-${step.text}`} className={step.state}><i aria-hidden /><span>{step.text}</span></li>
        ))}
        {steps.length === 0 && <li className="doing"><i aria-hidden /><span>Reaching the site</span></li>}
        {ready && <li className="doing"><i aria-hidden /><span>Orbit is ready to read</span></li>}
      </ol>
    </div>
  );
};

/*
 * The close: what the first read has, once every pass has been through.
 *
 * Results that landed after their own card had passed appear here, as lines
 * of one list. A late answer never takes the frame back to the pass it belongs
 * to, because going back is the thing this sequence does not do.
 */
const CloseFace: React.FC<{ passes: DeckReadPass[]; shown: number; failed?: string; kept: number; pages: number }> = ({ passes, shown, failed, kept, pages }) => {
  if (failed) {
    return (
      <div className="odeck-pass">
        <span className="odeck-read-ring" aria-hidden><i /></span>
        <strong>The read stopped early</strong>
        <small>WHY · ORBIT SAYS WHAT IT DID NOT GET</small>
        <em className="odeck-read-none">{failed}</em>
      </div>
    );
  }
  const lines = passes.filter((p) => landed(p) && p.line).slice(0, CLOSE_LINES);
  return (
    <div className="odeck-pass">
      <span className="odeck-read-ring done" aria-hidden><i /></span>
      <strong>Seven passes done</strong>
      <small>WHY · WHAT THE FIRST READ HAS SO FAR</small>
      <span className="odeck-read-at">{kept} field{plural(kept)} kept from {pages} page{plural(pages)}</span>
      <ol className="odeck-read-steps">
        {lines.slice(0, shown).map((p) => (
          <li key={p.key} className="done"><i aria-hidden /><span>{p.title}: {p.line}</span></li>
        ))}
      </ol>
      {lines.length === 0 && <em className="odeck-read-wait">The passes are still extracting. They report beside the deck.</em>}
    </div>
  );
};

export const DeckRead: React.FC<{
  work: DeckWork;
  num: string;
  /** Called once the sequence has closed. The deck resumes. */
  onDone?: () => void;
}> = ({ work, num, onDone }) => {
  const passes = work.passes || [];
  const total = passes.length;

  // The whole sequence. PRELUDE, then 0..total-1, then `total` for the close.
  // It only ever counts up.
  const [at, setAt] = useState<number>(PRELUDE);
  // The card on its way out, kept for the length of the slide so the two
  // cross rather than one being swapped for the other in place.
  const [leaving, setLeaving] = useState<React.ReactNode>(null);
  // Lines of the closing card, revealed one at a time.
  const [closeLines, setCloseLines] = useState(0);

  const started = useRef(Date.now());
  const appeared = useRef(Date.now());
  const slice = useRef(FLOOR);
  const landedAt = useRef(0);
  const mappedAt = useRef(0);
  const finished = useRef(false);
  // The last pass the sequence actually entered. The close reports on what ran,
  // not on what was planned.
  const reached = useRef(PRELUDE);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; });

  // Both counters only ever climb. A pass reports twice and the first report
  // carries the totals as they were when it STARTED, so taking either number
  // at face value made the foot of the card count backwards mid-read.
  const pagesSeen = useRef(0);
  pagesSeen.current = Math.max(pagesSeen.current, work.pagesRead || 0);
  const keptSeen = useRef(0);
  keptSeen.current = Math.max(keptSeen.current, passes.filter(landed).reduce((n, p) => n + p.found, 0));

  const mapped = (work.steps || []).some((s) => s.key === 'map' && s.state === 'done');
  const stopped = Boolean(work.failed);

  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    const done = onDoneRef.current;
    if (done) done();
  };

  /* Forward, one step, and never called with anything but the next one. */
  const advance = (next: number, now: number, face: React.ReactNode) => {
    setLeaving(face);
    appeared.current = now;
    landedAt.current = 0;
    // The time left, divided by the passes left. A prelude that ran long
    // shortens every remaining pass a little rather than costing the last
    // ones their turn, so the sequence always reaches seven.
    const remaining = Math.max(total - next, 1);
    slice.current = clamp((started.current + BUDGET - now) / remaining, FLOOR, CEIL);
    if (next < total) reached.current = next;
    setAt(next);
  };

  const tick = () => {
    if (finished.current) return;
    const now = Date.now();

    if (at >= total) {
      const lines = passes.filter((p) => landed(p) && p.line).length;
      const wanted = Math.min(lines, CLOSE_LINES);
      const revealed = Math.min(wanted, Math.floor((now - appeared.current) / CLOSE_LINE));
      if (revealed !== closeLines) setCloseLines(revealed);
      if (now < appeared.current + CLOSE_HOLD + wanted * CLOSE_LINE) return;
      finish();
      return;
    }

    if (at === PRELUDE) {
      if (mapped && !mappedAt.current) mappedAt.current = now;
      // A read that has already given up has nothing to open, so the prelude
      // hands straight to the close rather than sitting out its own clock.
      if (stopped) {
        advance(total, now, <PreludeFace work={work} ready={false} leaving />);
        return;
      }
      let out = started.current + PRELUDE_CAP;
      if (mappedAt.current) out = Math.min(mappedAt.current + AFTER_MAP, out);
      if (now < out) return;
      advance(0, now, <PreludeFace work={work} ready={mapped} leaving />);
      return;
    }

    const pass = passes[at];
    if (!pass) {
      finish();
      return;
    }
    const here = landed(pass);
    if (here && !landedAt.current) landedAt.current = now;
    let out = appeared.current + slice.current;
    // A pass that came back while its own card was up earns a beat to be read,
    // but never more than the band: the passes behind it are owed their turn.
    if (here) out = clamp(landedAt.current + AFTER_LANDING, appeared.current + FLOOR, appeared.current + CEIL);
    if (stopped) out = Math.min(out, appeared.current + FLOOR);
    if (now < out) return;
    // A read that has stopped finishes the pass on screen and then closes.
    // Racing the remaining four through in a second each would be motion
    // without work: those passes are not extracting, because nothing is.
    let next = at + 1;
    if (stopped) next = total;
    advance(next, now, <PassFace pass={pass} leaving />);
  };

  // The sequence asks the question on a clock and answers it from what the
  // server has said by then, so a pass landing anywhere in the deck is picked
  // up without any event restarting or reversing the walk.
  const tickRef = useRef(tick);
  useEffect(() => { tickRef.current = tick; });
  useEffect(() => {
    const t = window.setInterval(() => tickRef.current(), TICK);
    return () => window.clearInterval(t);
  }, []);

  /* Drop the outgoing card once it has finished sliding. */
  useEffect(() => {
    if (!leaving) return undefined;
    const t = window.setTimeout(() => setLeaving(null), SLIDE);
    return () => window.clearTimeout(t);
  }, [leaving]);

  if (total === 0) return null;

  let tag = `Pass ${Math.min(at + 1, total)} of ${total}`;
  if (at === PRELUDE) tag = 'Opening the site';
  if (at >= total) tag = 'The first read';

  let face = <CloseFace passes={passes} shown={closeLines} failed={work.failed} kept={keptSeen.current} pages={pagesSeen.current} />;
  if (at === PRELUDE) face = <PreludeFace key="prelude" work={work} ready={mapped} />;
  if (at >= 0 && at < total) face = <PassFace key={passes[at].key} pass={passes[at]} />;

  // The dot on screen, and how many are behind it. On the close both are a
  // count of what actually ran: a read that stopped in the prelude has been
  // through none of them.
  let active = at;
  let settled = Math.max(at, 0);
  if (at >= total) {
    active = -1;
    settled = total;
    if (work.failed) settled = reached.current + 1;
  }

  return (
    <>
      <div className="odeck-top">
        <span className="odeck-num">{num}</span>
        <span className="odeck-tag confirm">{tag}</span>
      </div>
      <h2>{work.title}</h2>
      {work.note && <p className="odeck-why">{work.note}</p>}
      <div className="odeck-read" aria-live="polite">
        <div className="odeck-pass-stage">
          {leaving}
          {face}
        </div>
        <div className="odeck-read-dots">
          {passes.map((p, i) => (
            <i key={p.key} className={dotClass(i, active, settled, p)} title={`${p.title}: ${p.line || p.state}`} />
          ))}
        </div>
      </div>
      <div className="odeck-foot read">
        <span>
          {pagesSeen.current} page{plural(pagesSeen.current)} read · {keptSeen.current} field{plural(keptSeen.current)} kept so far.
        </span>
        <button type="button" className="odeck-skip" onClick={finish}>Answer the questions instead</button>
      </div>
    </>
  );
};

export default DeckRead;
